import type { TrackUsage, TrackWhitelistRow, WhitelistStatus } from '@qlk/shared';
import { CONTENT_ID_LABEL, MUSIC_SOURCE_KIND_LABEL, WHITELIST_STATUSES } from '@qlk/shared';
import { History } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { coQuyen, useToi } from '../../../lib/auth.js';
import { DataTable, EmptyState, PageHeader, StatusBadge, type CotBang } from '../../../ui/index.js';
import { useBaiNhac, useDatWhitelist, useSuDungCuaBai, useWhitelistCuaBai } from '../api.js';
import {
  doDai,
  nhanGiayPhep,
  ngayGioVn,
  TONE_CONTENT_ID,
  TONE_NGUON,
} from '../components/dinhDang.js';
import { NutNgheThu } from '../components/NutNgheThu.js';

const NHAN_WHITELIST: Record<WhitelistStatus, string> = {
  whitelisted: 'Đã whitelist',
  requested: 'Đã gửi yêu cầu',
  missing: 'Thiếu whitelist',
};

const TONE_WHITELIST = { whitelisted: 'ok', requested: 'warn', missing: 'bad' } as const;

export function TrangChiTietBaiNhac() {
  const { id = '' } = useParams();
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'music', 'edit');

  const bai = useBaiNhac(id);
  const whitelist = useWhitelistCuaBai(id);
  const suDung = useSuDungCuaBai(id);
  const datWhitelist = useDatWhitelist(id);

  if (bai.isPending) return <p className="text-co-ban text-chu-phu">Đang tải bài nhạc…</p>;

  if (bai.error) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState
          loi
          title="Không mở được bài nhạc"
          description={bai.error.message}
          action={
            <Link to="/kho-nhac" className="text-co-ban font-semibold text-nhan">
              Về kho nhạc
            </Link>
          }
        />
      </div>
    );
  }

  const b = bai.data;
  const giayPhep = nhanGiayPhep(b);

  const cotWhitelist: CotBang<TrackWhitelistRow>[] = [
    {
      key: 'kenh',
      header: 'Kênh',
      render: (d) => <span className="font-semibold text-chu">{d.channelName}</span>,
    },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (d) => (
        <StatusBadge tone={TONE_WHITELIST[d.status]}>{NHAN_WHITELIST[d.status]}</StatusBadge>
      ),
    },
    {
      key: 'yeuCau',
      header: 'Gửi yêu cầu',
      render: (d) => (
        <span className="so-lieu text-chu-phu">
          {d.requestedAt ? ngayGioVn(d.requestedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'xacNhan',
      header: 'Xác nhận',
      render: (d) => (
        <span className="so-lieu text-chu-phu">
          {d.confirmedAt ? ngayGioVn(d.confirmedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'doi',
      header: 'Đổi trạng thái',
      render: (d) =>
        coSua ? (
          <select
            aria-label={`Đổi trạng thái whitelist của ${d.channelName}`}
            value={d.status}
            disabled={datWhitelist.isPending}
            onChange={(e) => {
              datWhitelist.mutate({
                channelId: d.channelId,
                status: e.target.value as WhitelistStatus,
              });
            }}
            className="vung-bam rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
          >
            {WHITELIST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {NHAN_WHITELIST[s]}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-chu-mo">—</span>
        ),
    },
  ];

  const cotSuDung: CotBang<TrackUsage>[] = [
    { key: 'kenh', header: 'Kênh', render: (d) => d.channelName },
    {
      key: 'video',
      header: 'Video',
      render: (d) => <span className="so-lieu text-chu-phu">{d.videoRef}</span>,
    },
    {
      key: 'luc',
      header: 'Dùng lúc',
      align: 'right',
      render: (d) => <span className="so-lieu text-chu-phu">{ngayGioVn(d.usedAt)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumb={['NỘI DUNG', 'Kho nhạc']}
        title={b.title}
        description={`${b.artist} · ${MUSIC_SOURCE_KIND_LABEL[b.sourceKind]} · ${b.sourceName}`}
        actions={
          <Link to="/kho-nhac" className="text-co-ban font-semibold text-chu-phu hover:text-chu">
            ← Kho nhạc
          </Link>
        }
      />

      <section className="mb-6 flex flex-wrap items-center gap-6 rounded-the border border-vien bg-be-mat p-6">
        <NutNgheThu title={b.title} previewUrl={b.previewUrl} />

        <dl className="grid flex-1 grid-cols-2 gap-5 @min-[600px]/noi-dung:grid-cols-3 @min-[1000px]/noi-dung:grid-cols-6">
          <div>
            <dt className="text-phu text-chu-mo">Thể loại</dt>
            <dd className="font-semibold text-chu">{b.genre ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-phu text-chu-mo">Tâm trạng</dt>
            <dd className="font-semibold text-chu">{b.moods.join(', ') || '—'}</dd>
          </div>
          <div>
            <dt className="text-phu text-chu-mo">BPM</dt>
            <dd className="so-lieu font-semibold text-chu">{b.bpm ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-phu text-chu-mo">Độ dài</dt>
            <dd className="so-lieu font-semibold text-chu">{doDai(b.durationSec)}</dd>
          </div>
          <div>
            <dt className="text-phu text-chu-mo">ISRC</dt>
            <dd className="so-lieu font-semibold text-chu">{b.isrc ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-phu text-chu-mo">Đã dùng</dt>
            <dd className="so-lieu font-semibold text-chu">{b.usageCount} lần</dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge tone={TONE_NGUON[b.sourceKind]} cham={false}>
            {b.sourceName}
          </StatusBadge>
          <StatusBadge tone={TONE_CONTENT_ID[b.contentIdStatus]}>
            {CONTENT_ID_LABEL[b.contentIdStatus]}
          </StatusBadge>
          <span className={`text-co-ban font-semibold ${giayPhep.mau}`}>{giayPhep.text}</span>
        </div>
      </section>

      <section aria-labelledby="wl-tieu-de" className="mb-6">
        <h2 id="wl-tieu-de" className="mb-4 text-tieu-de-khoi font-semibold text-chu">
          Whitelist theo kênh
        </h2>
        {whitelist.error ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState
              loi
              title="Không tải được whitelist"
              description={whitelist.error.message}
            />
          </div>
        ) : (
          <DataTable
            caption="Whitelist theo kênh"
            columns={cotWhitelist}
            rows={whitelist.data ?? []}
            keyOf={(d) => d.channelId}
            dangTai={whitelist.isPending}
            khiRong={
              <EmptyState
                title="Chưa có kênh nào đang hoạt động"
                description="Thêm kênh ở module Quản lý kênh để theo dõi whitelist."
              />
            }
          />
        )}
        {datWhitelist.error && (
          <p role="alert" className="mt-3 text-co-ban text-bad-chu">
            {datWhitelist.error.message}
          </p>
        )}
      </section>

      <section aria-labelledby="sd-tieu-de">
        <h2 id="sd-tieu-de" className="mb-4 text-tieu-de-khoi font-semibold text-chu">
          Lịch sử sử dụng
        </h2>
        {suDung.error ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState loi title="Không tải được lịch sử" description={suDung.error.message} />
          </div>
        ) : (
          <DataTable
            caption="Lịch sử sử dụng bài nhạc"
            columns={cotSuDung}
            rows={suDung.data ?? []}
            keyOf={(d) => d.id}
            dangTai={suDung.isPending}
            khiRong={
              <EmptyState
                icon={History}
                title="Bài này chưa được dùng"
                description="Lượt dùng được ghi khi module Sản xuất nội dung gán nhạc cho video."
              />
            }
          />
        )}
      </section>
    </>
  );
}
